import React from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { compPlanStatementSelector, providerPlanStatementSelector } from "src/redux/selectors/custom-selector";
import { useQueryFilterData } from "../filterQuery/useQueryFilterData";
import useCompPlanStatementService from "../planCompStatementReport/service";
import useProviderPlanStatementService from "../providerCompReport/service";
import useReportFilterService from "./reportFilterService";
import { setAdvanceFilterDropdownsAction } from "src/redux/actions/compplan-actions";
import { setProviderAdvanceFilterDropdownsAction } from "src/redux/actions/providerplan-actions";

// Stable empty fallbacks — never change reference.
const EMPTY_COLUMNS = [];
const EMPTY_FIELD_CONFIG_MAP = {};
const EMPTY_ACTIONS = {};

// Per-field allowed operators — matches the original static column definitions.
const SINGLE_SELECT_OPS = ["="];
const MULTI_SELECT_OPS = ["=", "IN"];
const FIELD_OPERATORS_MAP = {
  comp_plan_mstr_key: SINGLE_SELECT_OPS,
  period_mstr_key: SINGLE_SELECT_OPS,
  specialty_key: MULTI_SELECT_OPS,
  org_title_key: MULTI_SELECT_OPS,
  provider_key: MULTI_SELECT_OPS,
};

// Per-field default operator — overrides the first-of-type fallback when the field is added/selected.
const FIELD_DEFAULT_OPERATOR_MAP = {
  specialty_key: "IN",
  org_title_key: "IN",
  provider_key: "IN",
};

const FIELD_ALIAS_MAP = {
  // plan_comp_statement_report
  comp_plan_mstr_key: "comp_plan_name",
  period_mstr_key: "period_name",
  specialty_key: "specialty_name",
  org_title_key: "title_name",
  provider_key: "provider_name",
  // provider_comp_report
  comp_plan_key: "comp_plan_name",
  period_key: "period_name",
  title_key: "title_name",
};

// Maps redux items to { text, id, dataItemKey } shape for query-builder dropdowns.
const mapOptions = (items, { idKey, textKey, dataItemKey, includeSelectAll }) => {
  if (!Array.isArray(items)) return [];
  const validItems = items.filter((item) => item && typeof item === "object");
  // Suppress lone "Select All" sentinel when no real data has loaded yet.
  const hasRealItems = validItems.some((item) => item[idKey] !== 0);
  return validItems
    .filter((item) => (includeSelectAll && hasRealItems ? true : item[idKey] !== 0))
    .map((item) => {
      const text = item[textKey] ?? "";
      const isSelectAll = item[idKey] === 0 && text === "Select All";
      return {
        text,
        id: isSelectAll ? "Select All" : String(item[idKey] ?? ""),
        dataItemKey,
      };
    });
};

/**
 * Hook providing columns, fieldConfigMap, actions and operators for AdvanceQueryFilter.
 * Resolves plan-comp and provider-comp module data automatically; other modules use caller-supplied config.
 */
export const useAdvanceQueryFilterData = (config) => {
  const module = config?.module || "report";
  const filterTableName = config?.filterTableName || null;
  const filterDbName = config?.filterDbName || "procare_system";
  const [queryFilterData] = useQueryFilterData();
  const reportService = useCompPlanStatementService();
  const providerReportService = useProviderPlanStatementService();
  const filterService = useReportFilterService();

  const compPlanState = useSelector(compPlanStatementSelector, shallowEqual);
  const providerCompState = useSelector(providerPlanStatementSelector, shallowEqual);
  const dispatch = useDispatch();

  // Ref holding fresh service references every render so stable useMemos never hold stale closures.
  const advFetchRef = React.useRef({});
  advFetchRef.current = {
    fetchAdvCompPlan: (ctx, opts) => reportService.fetchAdvFilterCompPlan(ctx, opts),
    fetchAdvPeriods: (ctx, opts) => reportService.fetchAdvFilterPeriods(ctx, opts),
    fetchAdvSpecialities: (ctx, opts) => reportService.fetchAdvFilterSpecialities(ctx, opts),
    fetchAdvGroups: (ctx, opts) => reportService.fetchAdvFilterGroups(ctx, opts),
    fetchAdvProviders: (ctx, opts) => reportService.fetchAdvFilterProviders(ctx, opts),
    fetchAdvProvCompPlan: (ctx, opts) => providerReportService.fetchAdvFilterCompPlan(ctx, opts),
    fetchAdvProvPeriods: (ctx, opts) => providerReportService.fetchAdvFilterPeriods(ctx, opts),
    fetchAdvProvSpecialities: (ctx, opts) => providerReportService.fetchAdvFilterSpecialities(ctx, opts),
    fetchAdvProvGroups: (ctx, opts) => providerReportService.fetchAdvFilterGroups(ctx, opts),
    fetchAdvProvProviders: (ctx, opts) => providerReportService.fetchAdvFilterProviders(ctx, opts),
  };

  // Fallback when caller supplies everything via config props.
  const base = {
    operatorList: queryFilterData.operatorList,
    fetchOperators: queryFilterData.fetchOperators,
    columns: config?.columns ?? EMPTY_COLUMNS,
    fieldConfigMap: config?.fieldConfigMap ?? EMPTY_FIELD_CONFIG_MAP,
    actions: config?.actions ?? EMPTY_ACTIONS,
  };

  // Options computed from Redux advFilterDropdowns slice — never touch outer filter's Redux lists.
  const compPlanOptions = React.useMemo(
    () =>
      mapOptions(compPlanState.advFilterDropdowns?.compPlan || [], {
        idKey: "comp_plan_mstr_key",
        textKey: "comp_plan_name",
        dataItemKey: "comp_plan_mstr_key",
        includeSelectAll: false,
      }),
    [compPlanState.advFilterDropdowns?.compPlan]
  );

  const compPlanPeriodOptions = React.useMemo(
    () =>
      mapOptions(compPlanState.advFilterDropdowns?.period || [], {
        idKey: "period_mstr_key",
        textKey: "period_name",
        dataItemKey: "period_mstr_key",
        includeSelectAll: false,
      }),
    [compPlanState.advFilterDropdowns?.period]
  );

  const specialtyOptions = React.useMemo(
    () =>
      mapOptions(compPlanState.advFilterDropdowns?.speciality || [], {
        idKey: "specialty_key",
        textKey: "specialty_name",
        dataItemKey: "specialty_key",
        includeSelectAll: false,
      }),
    [compPlanState.advFilterDropdowns?.speciality]
  );

  const groupOptions = React.useMemo(
    () =>
      mapOptions(compPlanState.advFilterDropdowns?.group || [], {
        idKey: "org_title_key",
        textKey: "title_name",
        dataItemKey: "org_title_key",
        includeSelectAll: false,
      }),
    [compPlanState.advFilterDropdowns?.group]
  );

  const providerOptions = React.useMemo(
    () =>
      mapOptions(compPlanState.advFilterDropdowns?.provider || [], {
        idKey: "provider_key",
        textKey: "provider_name",
        dataItemKey: "provider_key",
        includeSelectAll: false,
      }),
    [compPlanState.advFilterDropdowns?.provider]
  );

  const planCompFieldConfigMap = React.useMemo(
    () => ({
      comp_plan_mstr_key: {
        data: compPlanOptions,
        setData: () => {},
        loading: compPlanState.advFilterDropdowns?.loadings?.compPlan ?? false,
        stateKey: "compPlanData",
        module: "compPlanValue",
        originalData: compPlanOptions,
        dataItemKey: "comp_plan_mstr_key",
        textKey: "comp_plan_name",
      },
      period_mstr_key: {
        data: compPlanPeriodOptions,
        setData: () => {},
        loading: compPlanState.advFilterDropdowns?.loadings?.period ?? false,
        stateKey: "compPlanPeriodData",
        module: "compPlanPeriodValue",
        originalData: compPlanPeriodOptions,
        dataItemKey: "period_mstr_key",
        textKey: "period_name",
      },
      specialty_key: {
        data: specialtyOptions,
        setData: () => {},
        loading: compPlanState.advFilterDropdowns?.loadings?.speciality ?? false,
        stateKey: "specialityData",
        module: "specialityValue",
        originalData: specialtyOptions,
        dataItemKey: "specialty_key",
        textKey: "specialty_name",
      },
      org_title_key: {
        data: groupOptions,
        setData: () => {},
        loading: compPlanState.advFilterDropdowns?.loadings?.group ?? false,
        stateKey: "groupData",
        module: "groupValue",
        originalData: groupOptions,
        dataItemKey: "org_title_key",
        textKey: "title_name",
      },
      provider_key: {
        data: providerOptions,
        setData: () => {},
        loading: compPlanState.advFilterDropdowns?.loadings?.provider ?? false,
        stateKey: "providerData",
        module: "providerValue",
        originalData: providerOptions,
        dataItemKey: "provider_key",
        textKey: "provider_name",
      },
    }),
    [
      compPlanOptions,
      compPlanPeriodOptions,
      specialtyOptions,
      groupOptions,
      providerOptions,
      compPlanState.advFilterDropdowns?.loadings,
    ]
  );

  const fieldGenRef = React.useRef({});
  const guarded = React.useCallback((field, fetchFn) => {
    return async (cascadeContext, { isWindowClosed } = {}) => {
      const gen = (fieldGenRef.current[field] ?? 0) + 1;
      fieldGenRef.current[field] = gen;
      const isStale = () => fieldGenRef.current[field] !== gen || isWindowClosed?.() === true;
      const result = await fetchFn(cascadeContext, { isStale });
      if (isStale()) return null;
      return result;
    };
  }, []);

  // Stable useMemo (empty deps) — calls through advFetchRef so they never hold stale closures.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const planCompActions = React.useMemo(
    () => ({
      comp_plan_mstr_key: guarded("comp_plan_mstr_key", (ctx, opts) => advFetchRef.current.fetchAdvCompPlan(ctx, opts)),
      period_mstr_key: guarded("period_mstr_key", (ctx, opts) => advFetchRef.current.fetchAdvPeriods(ctx, opts)),
      specialty_key: guarded("specialty_key", (ctx, opts) => advFetchRef.current.fetchAdvSpecialities(ctx, opts)),
      org_title_key: guarded("org_title_key", (ctx, opts) => advFetchRef.current.fetchAdvGroups(ctx, opts)),
      provider_key: guarded("provider_key", (ctx, opts) => advFetchRef.current.fetchAdvProviders(ctx, opts)),
    }),
    [guarded] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // API-driven columns: null = loading/not-fetched, [] = error/empty, [...] = loaded
  const [apiColumns, setApiColumns] = React.useState(null);

  React.useEffect(() => {
    if (!filterTableName) return;
    let cancelled = false;
    filterService
      .fetchFilterColumns(filterTableName, filterDbName)
      .then((cols) => {
        if (!cancelled) setApiColumns(cols ?? []);
      })
      .catch(() => {
        if (!cancelled) setApiColumns([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTableName, filterDbName]);

  // Boolean fields from the API → rendered as Switch toggles in the toolbar.
  const toggleFieldDefs = React.useMemo(
    () =>
      apiColumns
        ? apiColumns
            .filter((c) => c.type === "boolean" && !c.dropDownControl)
            .map((c) => ({ field: c.field, title: c.title }))
        : [],
    [apiColumns]
  );

  const apiQueryBuilderColumns = React.useMemo(() => {
    if (!apiColumns) return null;
    return apiColumns
      .filter((c) => !(c.type === "boolean" && !c.dropDownControl))
      .map((col) => ({
        title: col.title,
        field: col.field,
        type: col.type,
        dropDownControl: col.dropDownControl,
        isRequired: col.isRequired,
        alias: FIELD_ALIAS_MAP[col.field] ?? col.alias ?? col.field,
        gname: col.gname || "",
        dataItemKey: col.field,
        allowedOperators: FIELD_OPERATORS_MAP[col.field],
        defaultOperator: FIELD_DEFAULT_OPERATOR_MAP[col.field],
      }));
  }, [apiColumns]);

  // Fields with is_nullable=0 from the API → used as requiredFields for validation.
  // null means API hasn’t responded yet — validation is skipped until columns load.
  const apiRequiredFields = React.useMemo(
    () =>
      apiColumns
        ? apiColumns
            .filter((c) => c.isRequired && !(c.type === "boolean" && !c.dropDownControl))
            .map((c) => ({ field: c.field, label: c.title }))
        : null,
    [apiColumns]
  );

  // ── provider-comp-report-specific dropdown option arrays ─────────────────
  const providerCompPlanOptions = React.useMemo(
    () =>
      mapOptions(providerCompState.providerAdvFilterDropdowns?.compPlan || [], {
        idKey: "comp_plan_key",
        textKey: "comp_plan_name",
        dataItemKey: "comp_plan_key",
        includeSelectAll: false,
      }),
    [providerCompState.providerAdvFilterDropdowns?.compPlan]
  );

  const providerCompPeriodOptions = React.useMemo(
    () =>
      mapOptions(providerCompState.providerAdvFilterDropdowns?.period || [], {
        idKey: "period_key",
        textKey: "period_name",
        dataItemKey: "period_key",
        includeSelectAll: false,
      }),
    [providerCompState.providerAdvFilterDropdowns?.period]
  );

  const providerSpecialtyOptions = React.useMemo(
    () =>
      mapOptions(providerCompState.providerAdvFilterDropdowns?.speciality || [], {
        idKey: "specialty_key",
        textKey: "specialty_name",
        dataItemKey: "specialty_key",
        includeSelectAll: false,
      }),
    [providerCompState.providerAdvFilterDropdowns?.speciality]
  );

  const providerCompGroupOptions = React.useMemo(
    () =>
      mapOptions(providerCompState.providerAdvFilterDropdowns?.group || [], {
        idKey: "title_key",
        textKey: "title_name",
        dataItemKey: "title_key",
        includeSelectAll: false,
      }),
    [providerCompState.providerAdvFilterDropdowns?.group]
  );

  const providerCompProviderOptions = React.useMemo(
    () =>
      mapOptions(providerCompState.providerAdvFilterDropdowns?.provider || [], {
        idKey: "provider_key",
        textKey: "provider_name",
        dataItemKey: "provider_key",
        includeSelectAll: false,
      }),
    [providerCompState.providerAdvFilterDropdowns?.provider]
  );

  const providerCompFieldConfigMap = React.useMemo(
    () => ({
      comp_plan_key: {
        data: providerCompPlanOptions,
        setData: () => {},
        loading: providerCompState.providerAdvFilterDropdowns?.loadings?.compPlan ?? false,
        stateKey: "compPlanData",
        module: "compPlanValue",
        originalData: providerCompPlanOptions,
        dataItemKey: "comp_plan_key",
        textKey: "comp_plan_name",
      },
      period_key: {
        data: providerCompPeriodOptions,
        setData: () => {},
        loading: providerCompState.providerAdvFilterDropdowns?.loadings?.period ?? false,
        stateKey: "compPlanPeriodData",
        module: "compPlanPeriodValue",
        originalData: providerCompPeriodOptions,
        dataItemKey: "period_key",
        textKey: "period_name",
      },
      specialty_key: {
        data: providerSpecialtyOptions,
        setData: () => {},
        loading: providerCompState.providerAdvFilterDropdowns?.loadings?.speciality ?? false,
        stateKey: "specialityData",
        module: "specialityValue",
        originalData: providerSpecialtyOptions,
        dataItemKey: "specialty_key",
        textKey: "specialty_name",
      },
      title_key: {
        data: providerCompGroupOptions,
        setData: () => {},
        loading: providerCompState.providerAdvFilterDropdowns?.loadings?.group ?? false,
        stateKey: "groupData",
        module: "groupValue",
        originalData: providerCompGroupOptions,
        dataItemKey: "title_key",
        textKey: "title_name",
      },
      provider_key: {
        data: providerCompProviderOptions,
        setData: () => {},
        loading: providerCompState.providerAdvFilterDropdowns?.loadings?.provider ?? false,
        stateKey: "providerData",
        module: "providerValue",
        originalData: providerCompProviderOptions,
        dataItemKey: "provider_key",
        textKey: "provider_name",
      },
    }),
    [
      providerCompPlanOptions,
      providerCompPeriodOptions,
      providerSpecialtyOptions,
      providerCompGroupOptions,
      providerCompProviderOptions,
      providerCompState.providerAdvFilterDropdowns?.loadings,
    ]
  );

  // Routes through advFetchRef so the useMemo (empty deps) never holds stale closures.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const providerCompActions = React.useMemo(
    () => ({
      comp_plan_key: guarded("comp_plan_key", (ctx, opts) => advFetchRef.current.fetchAdvProvCompPlan(ctx, opts)),
      period_key: guarded("period_key", (ctx, opts) => advFetchRef.current.fetchAdvProvPeriods(ctx, opts)),
      specialty_key: guarded("specialty_key", (ctx, opts) => advFetchRef.current.fetchAdvProvSpecialities(ctx, opts)),
      title_key: guarded("title_key", (ctx, opts) => advFetchRef.current.fetchAdvProvGroups(ctx, opts)),
      provider_key: guarded("prov_provider_key", (ctx, opts) => advFetchRef.current.fetchAdvProvProviders(ctx, opts)),
    }),
    [guarded] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const providerCompColumns = React.useMemo(
    () => [
      {
        title: "Comp Plan",
        field: "comp_plan_key",
        type: "string",
        dropDownControl: true,
        isRequired: false,
        alias: "comp_plan_name",
        gname: "",
        dataItemKey: "comp_plan_key",
        allowedOperators: ["="],
      },
      {
        title: "Comp Plan Period",
        field: "period_key",
        type: "string",
        dropDownControl: true,
        isRequired: false,
        alias: "period_name",
        gname: "",
        dataItemKey: "period_key",
        allowedOperators: ["="],
      },
      {
        title: "Specialty",
        field: "specialty_key",
        type: "string",
        dropDownControl: true,
        isRequired: false,
        alias: "specialty_name",
        gname: "",
        dataItemKey: "specialty_key",
        allowedOperators: ["=", "IN"],
        defaultOperator: "IN",
      },
      {
        title: "Group",
        field: "title_key",
        type: "string",
        dropDownControl: true,
        isRequired: false,
        alias: "title_name",
        gname: "",
        dataItemKey: "title_key",
        allowedOperators: ["=", "IN"],
        defaultOperator: "IN",
      },
      {
        title: "Provider",
        field: "provider_key",
        type: "string",
        dropDownControl: true,
        isRequired: false,
        alias: "provider_name",
        gname: "",
        dataItemKey: "provider_key",
        allowedOperators: ["=", "IN"],
        defaultOperator: "IN",
      },
    ],
    []
  );

  if (module === "plan_comp_statement_report" && !config?.columns) {
    return {
      operatorList: queryFilterData.operatorList,
      fetchOperators: queryFilterData.fetchOperators,
      columns: apiQueryBuilderColumns ?? EMPTY_COLUMNS,
      fieldConfigMap: planCompFieldConfigMap,
      actions: planCompActions,
      cascadeFields: ["specialty_key", "org_title_key", "provider_key"],
      toggleFieldDefs,
      requiredFields: apiRequiredFields,
      clearFieldData: {
        comp_plan_mstr_key: () => {
          dispatch(setAdvanceFilterDropdownsAction({ compPlan: [] }));
        },
        period_mstr_key: () => {
          dispatch(setAdvanceFilterDropdownsAction({ period: [] }));
        },
        specialty_key: () => {
          dispatch(setAdvanceFilterDropdownsAction({ speciality: [] }));
        },
        org_title_key: () => {
          dispatch(setAdvanceFilterDropdownsAction({ group: [] }));
        },
        provider_key: () => {
          dispatch(setAdvanceFilterDropdownsAction({ provider: [] }));
        },
      },
    };
  }

  if (module === "provider_comp_report" && !config?.columns) {
    return {
      operatorList: queryFilterData.operatorList,
      fetchOperators: queryFilterData.fetchOperators,
      columns: filterTableName ? (apiQueryBuilderColumns ?? EMPTY_COLUMNS) : providerCompColumns,
      fieldConfigMap: providerCompFieldConfigMap,
      actions: providerCompActions,
      cascadeFields: ["specialty_key", "title_key", "provider_key"],
      // No filterTableName for provider, so the API doesn't supply boolean toggle columns.
      // Hardcode the Provider Detail toggle so the popup matches Plan Comp.
      toggleFieldDefs: filterTableName ? toggleFieldDefs : [{ field: "is_provider_detail", title: "Provider Detail" }],
      requiredFields: apiRequiredFields,
      clearFieldData: {
        comp_plan_key: () => {
          dispatch(setProviderAdvanceFilterDropdownsAction({ compPlan: [] }));
        },
        period_key: () => {
          dispatch(setProviderAdvanceFilterDropdownsAction({ period: [] }));
        },
        specialty_key: () => {
          dispatch(setProviderAdvanceFilterDropdownsAction({ speciality: [] }));
        },
        title_key: () => {
          dispatch(setProviderAdvanceFilterDropdownsAction({ group: [] }));
        },
        provider_key: () => {
          dispatch(setProviderAdvanceFilterDropdownsAction({ provider: [] }));
        },
      },
    };
  }

  return base;
};
